import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const TrainingResources = ({ className = '' }) => {
  const [activeVideo, setActiveVideo] = useState(null);
  const [completedVideos, setCompletedVideos] = useState(['quick-publish', 'pricing']);

  const trainingVideos = [
  {
    id: 'quick-publish',
    title: 'Publicación Rápida en 15 Segundos',
    duration: '2:30',
    category: 'Básico',
    description: 'Aprende a publicar bolsas sorpresa de manera eficiente usando plantillas y configuraciones predeterminadas.',
    thumbnail: "https://images.unsplash.com/photo-1601972605341-b039a4f3ada7",
    thumbnailAlt: 'Person using smartphone to quickly publish food items on mobile app interface',
    difficulty: 'Principiante',
    views: 1250,
    rating: 4.8
  },
  {
    id: 'pricing',
    title: 'Estrategias de Precios Efectivas',
    duration: '3:15',
    category: 'Intermedio',
    description: 'Maximiza tus ingresos con técnicas de pricing dinámico y descuentos estratégicos.',
    thumbnail: "https://images.unsplash.com/photo-1497520819034-51d530000955",
    thumbnailAlt: 'Calculator and price tags showing discount calculations for food pricing strategy',
    difficulty: 'Intermedio',
    views: 890,
    rating: 4.9
  },
  {
    id: 'qr-codes',
    title: 'Gestión de Códigos QR',
    duration: '1:45',
    category: 'Básico',
    description: 'Genera y gestiona códigos QR para facilitar la recogida de pedidos.',
    thumbnail: "https://images.unsplash.com/photo-1705300397099-80ba6a1ce6d8",
    thumbnailAlt: 'QR code being scanned with smartphone for food pickup verification',
    difficulty: 'Principiante',
    views: 675,
    rating: 4.7
  },
  {
    id: 'customer-service',
    title: 'Atención al Cliente Excelente',
    duration: '2:50',
    category: 'Avanzado',
    description: 'Técnicas para manejar quejas, mejorar ratings y fidelizar clientes.',
    thumbnail: "https://images.unsplash.com/photo-1614256291544-67c787620101",
    thumbnailAlt: 'Friendly restaurant staff member smiling while serving customer at counter',
    difficulty: 'Avanzado',
    views: 432,
    rating: 4.6
  },
  {
    id: 'sustainability',
    title: 'Prácticas de Sostenibilidad',
    duration: '3:00',
    category: 'Certificación',
    description: 'Implementa prácticas sostenibles para mejorar tu certificación Zero Waste.',
    thumbnail: "https://images.unsplash.com/photo-1503421960785-09113df4b1c1",
    thumbnailAlt: 'Eco-friendly restaurant kitchen with compost bins and sustainable food storage practices',
    difficulty: 'Intermedio',
    views: 789,
    rating: 4.9
  },
  {
    id: 'analytics',
    title: 'Análisis de Rendimiento',
    duration: '2:20',
    category: 'Avanzado',
    description: 'Interpreta métricas clave y optimiza tu estrategia basada en datos.',
    thumbnail: "https://images.unsplash.com/photo-1504868584819-f8e8b4b6d7e3",
    thumbnailAlt: 'Business analytics dashboard showing food waste reduction charts and performance metrics',
    difficulty: 'Avanzado',
    views: 543,
    rating: 4.8
  }];


  const regulatoryInfo = [
  {
    id: 'food-safety',
    title: 'Normativas de Seguridad Alimentaria',
    description: 'Cumplimiento de HACCP y regulaciones sanitarias mexicanas',
    icon: 'Shield',
    status: 'updated',
    lastUpdate: '15 Oct 2024'
  },
  {
    id: 'hygiene',
    title: 'Estándares de Higiene',
    description: 'Protocolos de limpieza y manipulación de alimentos',
    icon: 'Droplets',
    status: 'current',
    lastUpdate: '10 Oct 2024'
  },
  {
    id: 'data-protection',
    title: 'Protección de Datos',
    description: 'Cumplimiento de LFPDPPP y privacidad del cliente',
    icon: 'Lock',
    status: 'current',
    lastUpdate: '08 Oct 2024'
  }];


  const categories = ['Todos', 'Básico', 'Intermedio', 'Avanzado', 'Certificación'];
  const [selectedCategory, setSelectedCategory] = useState('Todos');

  const filteredVideos = selectedCategory === 'Todos' ?
  trainingVideos :
  trainingVideos?.filter((video) => video?.category === selectedCategory);

  const handleVideoComplete = (videoId) => {
    if (!completedVideos?.includes(videoId)) {
      setCompletedVideos((prev) => [...prev, videoId]);
    }
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'Principiante':
        return 'text-success bg-success/10';
      case 'Intermedio':
        return 'text-warning bg-warning/10';
      case 'Avanzado':
        return 'text-error bg-error/10';
      default:
        return 'text-muted-foreground bg-muted/10';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'updated':
        return 'text-accent bg-accent/10';
      case 'current':
        return 'text-success bg-success/10';
      default:
        return 'text-muted-foreground bg-muted/10';
    }
  };

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Training Videos Section */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-heading font-semibold text-foreground">
              Videos de Entrenamiento Express
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              Capacitación rápida de 2-3 minutos por tema
            </p>
          </div>
          
          <div className="flex items-center space-x-2">
            <Icon name="Play" size={20} color="var(--color-primary)" />
            <span className="text-sm font-medium text-primary">
              {completedVideos?.length}/{trainingVideos?.length} completados
            </span>
          </div>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap gap-2 mb-6">
          {categories?.map((category) =>
          <Button
            key={category}
            variant={selectedCategory === category ? 'default' : 'outline'}
            size="sm"
            onClick={() => setSelectedCategory(category)}>

              {category}
            </Button>
          )}
        </div>

        {/* Videos Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredVideos?.map((video) =>
          <div
            key={video?.id}
            className="bg-muted/30 rounded-lg overflow-hidden border border-border hover:border-primary/50 transition-all cursor-pointer"
            onClick={() => setActiveVideo(video)}>

              {/* Thumbnail */}
              <div className="relative aspect-video bg-muted">
                <img
                src={video?.thumbnail}
                alt={video?.thumbnailAlt}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.src = 'publicpublic/assets/images/slazzer-preview-av4u0-1762512006110.png';
                }} />

                
                {/* Play Button Overlay */}
                <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                  <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center">
                    <Icon name="Play" size={20} color="var(--color-primary-foreground)" />
                  </div>
                </div>
                
                {/* Duration Badge */}
                <div className="absolute bottom-2 right-2 bg-black/70 text-white px-2 py-1 rounded text-xs">
                  {video?.duration}
                </div>
                
                {/* Completed Badge */}
                {completedVideos?.includes(video?.id) &&
              <div className="absolute top-2 right-2 bg-success text-success-foreground p-1 rounded-full">
                    <Icon name="Check" size={14} />
                  </div>
              }
              </div>
              
              {/* Content */}
              <div className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className={`text-xs px-2 py-1 rounded-full ${getDifficultyColor(video?.difficulty)}`}>
                    {video?.difficulty}
                  </span>
                  <div className="flex items-center space-x-1">
                    <Icon name="Star" size={12} color="var(--color-warning)" />
                    <span className="text-xs text-muted-foreground">
                      {video?.rating}
                    </span>
                  </div>
                </div>
                
                <h3 className="font-medium text-foreground mb-2 line-clamp-2">
                  {video?.title}
                </h3>
                
                <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                  {video?.description}
                </p>
                
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>{video?.views} visualizaciones</span>
                  <span>{video?.category}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      {/* Regulatory Compliance */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-heading font-semibold text-foreground">
              Cumplimiento Regulatorio
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              Mantente actualizado con las normativas mexicanas
            </p>
          </div>
          
          <Button variant="outline" size="sm">
            <Icon name="Download" size={14} className="mr-2" />
            Descargar Guías
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {regulatoryInfo?.map((item) =>
          <div key={item?.id} className="p-4 bg-muted/30 rounded-lg border border-border">
              <div className="flex items-center justify-between mb-3">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name={item?.icon} size={20} color="var(--color-primary)" />
                </div>
                <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(item?.status)}`}>
                  {item?.status === 'updated' ? 'Actualizado' : 'Vigente'}
                </span>
              </div>
              
              <h3 className="font-medium text-foreground mb-2">
                {item?.title}
              </h3>
              
              <p className="text-sm text-muted-foreground mb-3">
                {item?.description}
              </p>
              
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">
                  Actualizado: {item?.lastUpdate}
                </span>
                <Button variant="ghost" size="sm">
                  <Icon name="ExternalLink" size={14} />
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
      {/* Video Modal */}
      {activeVideo &&
      <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <div className="bg-background rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h3 className="font-heading font-semibold text-foreground">
                {activeVideo?.title}
              </h3>
              <Button
              variant="ghost"
              size="icon"
              onClick={() => setActiveVideo(null)}>

                <Icon name="X" size={20} />
              </Button>
            </div>
            
            <div className="p-6">
              {/* Video Player Placeholder */}
              <div className="aspect-video bg-muted rounded-lg flex items-center justify-center mb-4">
                <div className="text-center">
                  <Icon name="Play" size={64} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
                  <p className="text-muted-foreground">
                    Reproductor de video - {activeVideo?.duration}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-muted-foreground mb-2">
                    {activeVideo?.description}
                  </p>
                  <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                    <span>Duración: {activeVideo?.duration}</span>
                    <span>Dificultad: {activeVideo?.difficulty}</span>
                    <span>Rating: {activeVideo?.rating}/5</span>
                  </div>
                </div>
                
                <Button
                variant="default"
                onClick={() => {
                  handleVideoComplete(activeVideo?.id);
                  setActiveVideo(null);
                }}>

                  <Icon name="Check" size={16} className="mr-2" />
                  Marcar como Completado
                </Button>
              </div>
            </div>
          </div>
        </div>
      }
    </div>);

};

export default TrainingResources;